import { ModelLoaderOptions, ModelViewer, getBoundingBoxSize, maxDimension, useModelViewerLoader } from "@centricsoftware/model-viewer";
import { getMtlFileUrl } from "csi/model/document";
import RightPane from "csi/page/RightPane";
import { useStore } from "csi/state/hooks";
import { Box, FlexRow, LoadingPlaceholder } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect } from "react";
import { getFileVaultRequestUrl } from "src/c8-platform/core/request";
import ThreeDViewerToolbar from "../toolbars/ThreeDViewerToolBar";
import { getDracoPath, getMaterialDefaultPath, getResourceURLModifier, getRhinoPath } from "../utility/resources";


export const ThreeDViewer: FunctionComponent = observer(() => {
	const rootStore = useStore();
	const { threeDStore } = rootStore;
	const { 
		canvasStore,
		revisionNode,
		annotationsStore,
		modelUrl,
		fileType,
	 } = threeDStore;
	
	const dracoDecoderPath = getDracoPath();
	const rhinoLibraryPath = getRhinoPath();
	const materialDefaultGeometryPath = getMaterialDefaultPath();
	const resourceURLModifier = getResourceURLModifier(revisionNode);
	const loc = useLocale();
	const loaderTitle = loc.resource("LoadingEllipsis");
	const loaderSubtitle = loc.resource("LoadingInProgress", [loc.resource("3DViewer")]);
	annotationsStore.modelViewerMaxDimension = maxDimension;
	annotationsStore.modelViewerGetBoundingBoxSize = getBoundingBoxSize;

	const options: ModelLoaderOptions = {
		resourceURLModifier,
		fileType,
		dracoDecoderPath,
		rhinoLibraryPath,
		materialDefaultGeometryPath,
		on3DinitializedState: (state) => {
			const viewport = state.get().viewport;
			const { commentsDetails, setAnnotationPins } = annotationsStore;
			const { variantName } = canvasStore;
            
			annotationsStore.modelViewerViewport = viewport;
			setAnnotationPins(commentsDetails);

			// set the default variant if there is one
			if (variantName) {
				canvasStore.onChangeModelVariant(variantName);
			}
		},
	};
    
	//if the fileType is obj and there is an mtl file then add the mtl file to the options
	const mtlUrl = (fileType === "obj") ? getMtlFileUrl(revisionNode) : null;
	if (mtlUrl) {
		options["objMaterialFile"] = getFileVaultRequestUrl(mtlUrl, false);
	}	

	const [store, error] = useModelViewerLoader(modelUrl, options, threeDStore.initializeModelViewer);

	const onDoubleClick = () => {
		annotationsStore.modelViewerSetUpdateAnnotationPosition(true);
	};

	useEffect(() => {
		window.addEventListener("dblclick", onDoubleClick);
		return () => {
			window.removeEventListener("dblclick", onDoubleClick);
		};
	}, []);

	if (!store) {
		return <Loader subtitle={loaderSubtitle} title={loaderTitle} />;
	}

	return (
		<FlexRow
			flexGrow={1}
			growNoOverflow
			height="100%"
		>
			<Box growNoOverflow>
				<ThreeDViewerToolbar />
				<ModelViewer 
					loadingPlaceholder={<Loader subtitle={loaderSubtitle} title={loaderTitle} />}
					store={store}
				/>
			</Box>
			<RightPane />
		</FlexRow>
	);
});

function Loader({ title, subtitle }) {
	return (
		<Box
			alignItems="center"
			display="flex"
			growNoOverflow
			justifyContent="center"
		>
			<LoadingPlaceholder
				subtitleProps={{ text: subtitle }}
				titleProps={{ text: title }}
			/>
		</Box>
	);
}


export default ThreeDViewer;